import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { comments as seedComments } from '../data/comments';
import { communities } from '../data/communities';
import { people } from '../data/people';
import { posts as seedPosts } from '../data/posts';
import { pulses as seedPulses } from '../data/pulses';
import type { Comment, L10n, Post, PostContext, PulseRing, SportCategory } from '../types';
interface NewPostDraft {
  media: string;
  caption: string;
  category: SportCategory;
  activity?: string;
  location?: string;
}
interface NewPulseDraft {
  media: string;
  kind: SportCategory;
  label: string;
  metric?: string;
}
interface SocialValue {
  posts: Post[];
  pulses: PulseRing[];
  commentsFor: (postId: string) => Comment[];
  commentCount: (postId: string) => number;
  isLiked: (postId: string) => boolean;
  likeCount: (post: Post) => number;
  toggleLike: (postId: string) => void;
  isSaved: (postId: string) => boolean;
  toggleSave: (postId: string) => void;
  isFollowing: (personId: string) => boolean;
  toggleFollow: (personId: string) => void;
  followerCount: (personId: string) => number;
  markPulseSeen: (pulseId: string) => void;
  publishPost: (draft: NewPostDraft) => string;
  publishPulse: (draft: NewPulseDraft) => string;
  likeComment: (commentId: string) => void;
  isCommentLiked: (commentId: string) => boolean;
  addComment: (postId: string, text: string) => void;
  isMember: (communityId: string) => boolean;
  toggleMembership: (communityId: string) => void;
  memberCount: (communityId: string) => number;
}
const SocialContext = createContext<SocialValue | null>(null);
function bilingual(value: string): L10n {
  return {
    'pt-BR': value,
    'en-US': value
  };
}
export function SocialProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [posts, setPosts] = useState<Post[]>(seedPosts);
  const [pulses, setPulses] = useState<PulseRing[]>(seedPulses);
  const [commentList, setCommentList] = useState<Comment[]>(seedComments);
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [saved, setSaved] = useState<Record<string, boolean>>({});
  const [likedComments, setLikedComments] = useState<Record<string, boolean>>({});
  const [follows, setFollows] = useState<Record<string, boolean>>(() => people.reduce<Record<string, boolean>>((acc, person) => {
    acc[person.id] = person.following;
    return acc;
  }, {}));
  const [memberships, setMemberships] = useState<Record<string, boolean>>(() => communities.reduce<Record<string, boolean>>((acc, community) => {
    acc[community.id] = community.joinedByDefault;
    return acc;
  }, {}));
  const toggle = useCallback((setter: React.Dispatch<React.SetStateAction<Record<string, boolean>>>, key: string) => setter(prev => ({
    ...prev,
    [key]: !prev[key]
  })), []);
  const value = useMemo<SocialValue>(() => {
    const commentsFor = (postId: string) => commentList.filter(comment => comment.postId === postId);
    return {
      posts,
      pulses,
      commentsFor,
      commentCount: (postId: string) => commentsFor(postId).reduce((acc, comment) => acc + 1 + comment.replies.length, 0),
      isLiked: (postId: string) => Boolean(liked[postId]),
      likeCount: (post: Post) => post.likes + (liked[post.id] ? 1 : 0),
      toggleLike: (postId: string) => toggle(setLiked, postId),
      isSaved: (postId: string) => Boolean(saved[postId]),
      toggleSave: (postId: string) => toggle(setSaved, postId),
      isFollowing: (personId: string) => Boolean(follows[personId]),
      toggleFollow: (personId: string) => toggle(setFollows, personId),
      followerCount: (personId: string) => {
        const person = people.find(p => p.id === personId);
        if (!person) return 0;
        const base = person.counts.followers - (person.following ? 1 : 0);
        return base + (follows[personId] ? 1 : 0);
      },
      markPulseSeen: (pulseId: string) => setPulses(prev => prev.map(ring => ring.id === pulseId ? {
        ...ring,
        seen: true
      } : ring)),
      publishPost: (draft: NewPostDraft) => {
        const id = `p-${Date.now()}`;
        const context: PostContext = {
          category: draft.category,
          activity: draft.activity ? bilingual(draft.activity) : undefined
        };
        const post: Post = {
          id,
          authorId: 'me',
          media: draft.media,
          caption: bilingual(draft.caption),
          context,
          likes: 0,
          comments: 0,
          hoursAgo: 0,
          location: draft.location ? bilingual(draft.location) : undefined
        };
        setPosts(prev => [post, ...prev]);
        return id;
      },
      publishPulse: (draft: NewPulseDraft) => {
        const id = `pulse-me-${Date.now()}`;
        const ring: PulseRing = {
          id,
          authorId: 'me',
          seen: false,
          items: [{
            id: `${id}-1`,
            media: draft.media,
            kind: draft.kind,
            label: bilingual(draft.label),
            metric: draft.metric ? bilingual(draft.metric) : undefined
          }]
        };
        setPulses(prev => [ring, ...prev.filter(item => item.authorId !== 'me')]);
        return id;
      },
      likeComment: (commentId: string) => toggle(setLikedComments, commentId),
      isCommentLiked: (commentId: string) => Boolean(likedComments[commentId]),
      addComment: (postId: string, text: string) => setCommentList(prev => [...prev, {
        id: `c-${Date.now()}`,
        postId,
        authorId: 'me',
        text: bilingual(text),
        hoursAgo: 0,
        likes: 0,
        replies: []
      }]),
      isMember: (communityId: string) => Boolean(memberships[communityId]),
      toggleMembership: (communityId: string) => toggle(setMemberships, communityId),
      memberCount: (communityId: string) => {
        const community = communities.find(item => item.id === communityId);
        if (!community) return 0;
        const base = community.members - (community.joinedByDefault ? 1 : 0);
        return base + (memberships[communityId] ? 1 : 0);
      }
    };
  }, [posts, pulses, commentList, liked, saved, likedComments, follows, memberships, toggle]);
  return <SocialContext.Provider value={value} data-id="element-1269" data-mp="187oh2375irll">{children}</SocialContext.Provider>;
}
export function useSocial(): SocialValue {
  const ctx = useContext(SocialContext);
  if (!ctx) throw new Error('useSocial must be used inside SocialProvider');
  return ctx;
}